// 🤖 HOOK DE ANÁLISE IA
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/hooks/useIAAnalise.ts

import { useState } from "react";
import { removerEscoresAtuaisPosTx } from "../../../shared/utils/escoresPosTx";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, auth } from "../../../lib/firebase";
import { callAnalyze, callAnalyzeStream } from "../../../lib/analyzeClient";
import { Paciente } from "../../../types";
import { AnaliseIA, CacheAnalise, DadosAnonimizados } from "../types";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
// B351 (AI_153) — política do few-shot por modalidade (fonte única + testes).
import { modalidadeUsaFewShotMECR } from "../services/politicaFewShotMECR";
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
// B351 passo 5b (AI_153) — prompts próprios da modalidade doador vivo.
import {
  gerarPromptAnaliseDoador,
  INSTRUCOES_ETAPA1_DOADOR,
  DIMENSOES_ETAPA2_DOADOR,
  FORMATO_SAIDA_DOADOR,
} from "../services/promptsDoador";
import {
  avisosContradicaoFonte,
  extrairTextoAnalise,
} from "../services/avisosContradicaoFonte";
import {
  derivarStatusGeral,
  derivarGravidade,
  derivarUrgencia,
  derivarAdequabilidadeDoador,
  statusGeralDeAdequabilidade,
} from "../services/derivarStatusGeral";
import {
  minimizarTextoMECR,
  minimizarListaMECR,
  minimizarSugestaoMECR,
} from "../services/minimizarExemploMECR";
import { minimizarDadosParaCruzamento } from "../services/minimizarDadosCruzamento";
import {
  gerarPromptAnaliseCompleta,
  gerarPromptAnaliseGeralRefinado,
  VERSAO_PROMPT,
} from "../services/promptsMedicos";
import {
  obterContextoGuidelinesComDocs,
  obterContextoGuidelinesDoador,
  reRetrievalCondicional,
} from "../rag";
import { removerUndefined } from "../../../shared/utils/removerUndefined";
import {
  montarProvenienciaGeracao,
  ProvenienciaBackend,
} from "../services/provenienciaGeracao";
import { examesComMeldRotulado } from "../services/valoresLabParaIA";

/**
 * 🎯 HOOK PRINCIPAL DE ANÁLISE IA
 *
 * Funcionalidades:
 * - Gerar análise completa do paciente
 * - Controlar cache (1 análise por dia)
 * - Integrar com API Anthropic
 * - Salvar resultados no Firestore
 * - Gerenciar estados (loading, erro, sucesso)
 */
export function useIAAnalise() {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [analiseAtual, setAnaliseAtual] = useState<AnaliseIA | null>(null);
  const [progresso, setProgresso] = useState<string>("");
  // 🌊 B389 (AI_157) — texto da Etapa 1 enquanto o modelo escreve (só na
  // memória da tela; nunca vai ao Firestore). Vazio fora da geração.
  const [rascunho, setRascunho] = useState<string>("");

  /**
   * 📊 VERIFICAR SE PODE GERAR NOVA ANÁLISE
   * Limitação: 1 análise por dia por paciente
   */
  const verificarCache = async (pacienteId: string): Promise<CacheAnalise> => {
    try {
      const analisesRef = collection(db, "analises-ia");
      const q = query(
        analisesRef,
        where("pacienteId", "==", pacienteId),
        orderBy("dataAnalise", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          pacienteId,
          podeGerar: true,
        };
      }

      const ultimaAnalise = snapshot.docs[0].data();
      const dataUltima = new Date(ultimaAnalise.dataAnalise);
      const agora = new Date();

      // Calcular diferença em horas
      const diffHoras =
        (agora.getTime() - dataUltima.getTime()) / (1000 * 60 * 60);

      if (diffHoras < 24) {
        const proximaDisponivel = new Date(
          dataUltima.getTime() + 24 * 60 * 60 * 1000
        );
        const horasRestantes = Math.ceil(24 - diffHoras);

        return {
          pacienteId,
          ultimaAnalise: ultimaAnalise.dataAnalise,
          podeGerar: false,
          proximaDisponivel: proximaDisponivel.toISOString(),
          motivoBloqueio: `Aguarde ${horasRestantes}h para nova análise`,
        };
      }

      return {
        pacienteId,
        ultimaAnalise: ultimaAnalise.dataAnalise,
        podeGerar: true,
      };
    } catch (error) {
      console.error("❌ Erro ao verificar cache:", error);
      // Em caso de erro, permitir análise
      return {
        pacienteId,
        podeGerar: true,
      };
    }
  };

  /**
   * 🤖 CHAMAR API ANTHROPIC - ANÁLISE EM 2 ETAPAS
   * Etapa 1: Chain-of-Diagnosis (raciocínio clínico estruturado)
   * Etapa 2: Self-Reflection (revisão crítica e refinamento)
   */
  const chamarAPIAnthropic = async (
    prompt: string,
    dadosOriginais?: string,
    // B351 passo 5b (AI_153) — enum de CONTROLE (não carrega conteúdo clínico).
    // O backend usa-o para escolher o system prompt da modalidade; sem ele, a
    // análise do doador receberia o prompt do RECEPTOR, que ORDENA situação
    // especial e candidatura pré-Tx.
    modalidade?: string
  ): Promise<AnaliseIA> => {
    try {
      // ═══════════════════════════════════════════
      // ETAPA 1: CHAIN-OF-DIAGNOSIS
      // ═══════════════════════════════════════════
      console.log("🤖 [Etapa 1/2] Chain-of-Diagnosis...");

      const dataHoje = new Date().toLocaleDateString("pt-BR");
      // B351 passo 5b (AI_153) — a modalidade decide a POSTURA da Etapa 1. No
      // doador não existe "período pós-Tx" nem lógica de isquemia-reperfusão: a
      // linha do período e as 7 regras de raciocínio abaixo são do receptor e
      // seriam contradição vinda do próprio prompt.
      const ehDoador = ehDoadorFigado(modalidade);
      const promptEtapa1 = ehDoador
        ? `${prompt}

DATA DESTA ANÁLISE: ${dataHoje}
${INSTRUCOES_ETAPA1_DOADOR}`
        : `${prompt}

DATA DESTA ANÁLISE: ${dataHoje}
REGRA OBRIGATÓRIA: O período pós-transplante informado no prompt (ex: "4 dias pós-Tx") é calculado automaticamente pelo sistema usando a data de HOJE. NÃO recalcule usando a data do último exame. A data do último exame pode ser anterior a hoje porque exames ainda não foram digitados. O período do sistema é SEMPRE correto.

INSTRUÇÕES: Você é um hepatologista e cirurgião de transplante hepático com 20 anos de experiência. Um colega te apresenta este caso para discussão. Raciocine como faria numa discussão clínica entre especialistas — direto, objetivo, com julgamento clínico.

COMO RACIOCINAR:
1. Antes de classificar qualquer achado como "anormal" ou "preocupante", pergunte-se: isso é esperado para o CONTEXTO deste paciente? 
   - Se pós-Tx D0-D7: transaminases elevadas são IRI até prova em contrário, NÃO "disfunção"
   - Se cirrótico com descompensação: plaquetopenia é hiperesplenismo, NÃO problema novo
   - Se pós-Tx tardio: elevação de enzimas tem significado diferente do pós-Tx precoce
   
2. Separe o que é ACHADO REAL do que é RUÍDO ou ACHADO ESPERADO para o contexto. Muitos valores "alterados" são o padrão normal para aquele paciente naquele momento.

3. Foque nos 3-5 problemas que REALMENTE importam AGORA. Não liste 15 diagnósticos remotos para parecer completo. O que mudaria sua conduta hoje?

4. Para cada exame que recomendar, pergunte: "este exame vai mudar minha conduta?" Se não, não peça.

5. Para cada medicamento que sugerir, pergunte: "a indicação está documentada nos dados?" Se não está, não prescreva.

6. TENDÊNCIAS importam mais que valores isolados. AST subindo mas lactato estável tem significado diferente de AST subindo com lactato subindo.

7. Cite exames existentes com tipo, data e achados. Não fale de exames como se não existissem quando eles estão nos dados.

Discuta o caso de forma estruturada mas natural — como falaria com o colega, não como preencheria um formulário.`;

      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      // Etapa 1 mantém comportamento de falha hard: throw cai no catch
      // externo do orquestrador (mensagem "Falha ao processar análise com IA")
      let data1: {
        content: Array<{ type: string; text: string }>;
        _proveniencia?: ProvenienciaBackend;
      };
      try {
        // 🌊 B389 (AI_157) — em FLUXO: o rascunho vai aparecendo na tela.
        data1 = await callAnalyzeStream<{
          content: Array<{ type: string; text: string }>;
          _proveniencia?: ProvenienciaBackend;
        }>(
          {
            prompt: promptEtapa1,
            useThinking: true,
            etapa: "chain-of-diagnosis",
            // esforço: definido no SERVIDOR (ESFORCO_RACIOCINIO em api/analyze.js)
            modalidade,
          },
          setRascunho
        );
      } catch (err: any) {
        console.error("❌ Erro na Etapa 1:", err.message);
        throw new Error(`API Error Etapa 1: ${err.message}`);
      }

      const analiseEtapa1 = data1.content
        .filter((c: any) => c.type === "text")
        .map((c: any) => c.text)
        .join("\n");

      console.log("✅ Etapa 1 concluída!");

      // ═══════════════════════════════════════════
      // 🔄 RE-RETRIEVAL CONDICIONAL
      // ═══════════════════════════════════════════
      // B256 (AI_115): DESLIGADO p/ latência/custo (OK de Leonardo; era um dos
      // "logs extras" que ele lembra não existir na versão rápida/excelente).
      // A Etapa 1 já recebe o RAG inicial robusto. Reverter = flag p/ true.
      const RE_RETRIEVAL_ATIVO = false;
      setProgresso("Refinando análise...");
      let contextoComplementar = "";
      if (RE_RETRIEVAL_ATIVO) {
        try {
          contextoComplementar = await reRetrievalCondicional(analiseEtapa1, []);
        } catch (err) {
          console.warn("⚠️ Re-retrieval falhou, continuando sem:", err);
        }
      }

      // ═══════════════════════════════════════════
      // ETAPA 2: SELF-REFLECTION + ANÁLISE FINAL
      // ═══════════════════════════════════════════
      console.log("🔍 [Etapa 2/2] Self-Reflection e Análise Final...");
      setProgresso("Refinando análise (Etapa 2/2)...");

      // Montar bloco de dados originais para cruzamento (se disponível)
      const blocoValidacao = dadosOriginais
        ? `\n═══ DADOS ORIGINAIS DO PACIENTE (para cruzamento) ═══\n${dadosOriginais}\n═══ FIM DOS DADOS ORIGINAIS ═══\n`
        : "";

      // Dimensão 5 condicional: regras completas para pós-Tx, mínimas para pré-Tx
      const isPosTx =
        dadosOriginais && dadosOriginais.includes('"tempoTransplante"');

      const dimensao5Completa = `DIMENSÃO 5 — TEMPORALIDADE PÓS-TRANSPLANTE
Se o paciente é transplantado, verifique:
- O período pós-Tx INFORMADO NO PROMPT (ex: "4 dias pós-Tx") é calculado automaticamente pelo sistema usando a data de HOJE, não a data do último exame. O último exame pode ser de dias atrás porque exames ainda não foram digitados. CONFIE no período informado pelo sistema. NÃO recalcule usando a data do último exame.

CURVA NORMAL DO PÓS-OPERATÓRIO IMEDIATO (D0-D60):
Antes de classificar qualquer achado como "disfunção" ou "complicação", verificar se está dentro do ESPERADO:
- AST/ALT: SEMPRE sobem em D+1 por IRI. Pico habitual em D1-D3. AST até 1500-2000 U/L nos primeiros 3 dias = IRI esperada, NÃO é "disfunção grave"
- BT: Hiperbilirrubinemia leve (até 5-8 mg/dL) é comum em D1-D3. Só é preocupante se >10 mg/dL em D7 (critério Olthoff)
- INR: Pode estar elevado em D1-D3 pelo tempo de isquemia do enxerto. Avaliar tendência, não valor isolado
- Plaquetas: Trombocitopenia pré-Tx por hiperesplenismo MELHORA progressivamente após Tx. Plaquetas subindo = bom sinal, NÃO é problema ativo novo. Queda nas primeiras 48h pode ser consumo cirúrgico + hemodiluição — só é preocupante se associada a fibrinogênio baixo e D-dímero alto (CID)
- Creatinina: Elevação leve (1,0-1,5 mg/dL) é comum por efeito hemodinâmico + introdução de tacrolimus
- Lactato: NORMAL = sem hipoperfusão sistêmica = dado FAVORÁVEL que deve ser valorizado
- GGT: Elevação de GGT é FISIOLÓGICA em TODO receptor de transplante hepático nas primeiras 4-8 semanas (D0-D60), com curva mais lenta e tardia que transaminases — indução enzimática por tacrolimus e corticoides. GGT elevada ou em ascensão ISOLADA NUNCA justifica diagnóstico de colestase nem investigação biliar — independentemente de qualquer fator de risco anatômico presente (anastomose sob tensão, variante técnica, etc). Fatores de risco anatômicos são alertas estruturais, não transformam GGT fisiológica em patológica. Investigar complicação biliar SOMENTE se presentes CRITÉRIOS CONJUNTOS: FA em ascensão + BT progressivamente >3 mg/dL + achado em imagem (dilatação de vias biliares) OU sintomas clínicos (febre, icterícia progressiva, colúria). NUNCA recomendar ColangioRM ou CPRE baseado em GGT isolada no pós-Tx D0-D60.

HEMOGLOBINA NO PÓS-TX IMEDIATO — NÃO ALARMAR PREMATURAMENTE:
- Queda de 2-3 g/dL nas primeiras 48h é ESPERADA (hemodiluição, perdas cirúrgicas, coletas seriadas)
- Hb 11→8,5 g/dL em D+1/D+2 é o padrão habitual de uma hepatectomia total com implante
- Alarme hemorrágico REAL exige: Hb <7 g/dL, OU instabilidade hemodinâmica, OU dreno com débito hemático ativo, OU necessidade de vasopressor
- NÃO classificar queda esperada de Hb como "anemia progressiva com suspeita de sangramento ativo" sem sinais adicionais
- Indicação de transfusão no pós-Tx: Hb <7 g/dL em paciente estável, ou <8 g/dL se sangramento ativo ou comorbidade cardíaca

LÓGICA DE TAH (TROMBOSE DE ARTÉRIA HEPÁTICA) — REGRA CRÍTICA:
- TAH causa elevação sustentada ou ascendente de AST/ALT com lactato subindo
- Se transaminases estão em QUEDA progressiva (ex: AST 972→399→225) + lactato NORMAL → a artéria hepática está PÉRVIA por definição fisiológica
- NUNCA sugerir "excluir TAH urgente" quando a curva enzimática demonstra perfusão adequada do enxerto
- Doppler hepático no D+1-D+2 é PROTOCOLAR (rotina do centro), não "urgente por suspeita de TAH" — a indicação é diferente
- Suspeitar de TAH apenas se: AST em elevação após D+3, OU transaminases estagnadas em valores muito altos (>3000), OU lactato em ascensão, OU ausência de produção biliar

CRITÉRIOS DE ALARME REAL no pós-Tx imediato (só classificar como disfunção se presentes):
- AST >2000 U/L nos primeiros 7 dias (critério Olthoff para EAD)
- BT >10 mg/dL em D7
- INR ≥1,6 em D7
- Lactato em ELEVAÇÃO progressiva (hipoperfusão)
- Transaminases SUBINDO após D3 (quando deveriam estar caindo)
- Ausência de produção biliar pelo dreno (se presente)

TOM DA ANÁLISE NO D0-D7:
- Se os labs estão dentro da curva esperada de IRI → classificar como "Evolução pós-operatória esperada com lesão de isquemia-reperfusão em monitoramento" — NÃO como "disfunção grave"
- Doppler hepático no D+1-D+2 é PROTOCOLAR (rotina pós-Tx), não "urgente por disfunção"
- Gravidade deve ser MODERADA (monitoramento ativo) se dentro da curva, não GRAVE

Se fora da curva esperada (AST >2000, lactato subindo, INR piorando após D3):
- Aí sim classificar como disfunção e acionar investigação de emergência (TAH, EAD, rejeição hiperaguda)

Os diagnósticos diferenciais são adequados ao período?
* 0-30 dias: EAD, TAH, rejeição hiperaguda, sangramento, leak biliar
* 1-6 meses: rejeição aguda celular, CMV/EBV, estenose biliar, TAH tardia
* 6-12 meses: rejeição aguda tardia, recidiva de doença de base, toxicidade de imunossupressores
* >12 meses: rejeição crônica (vanishing bile duct), recidiva viral, malignidades de novo
- Alvo de tacrolimus está adequado ao período?
* 0-3 meses: 8-12 ng/mL
* 3-6 meses: 6-10 ng/mL
* 6-12 meses: 5-8 ng/mL
* >12 meses: 4-6 ng/mL
- Se o paciente tem TAH documentada em exame, NÃO solicitar Doppler para "investigar TAH"`;

      const dimensao5Minima = `DIMENSÃO 5 — TEMPORALIDADE
- Verificar se condutas são adequadas ao momento clínico do paciente (pré-Tx vs acompanhamento).
- Se pré-Tx: diagnósticos diferenciais e condutas devem focar na doença de base, não em complicações pós-operatórias.`;

      const dimensao5 = isPosTx ? dimensao5Completa : dimensao5Minima;
      // B351 passo 5b (AI_153) — o audit do DOADOR tem 9 dimensões próprias
      // (as 11 do receptor são sobre cirrose, pós-Tx e oncologia) e um formato
      // de saída próprio, com o selo de adequabilidade no lugar de
      // Gravidade/Urgência. A persona de auditor vem daqui: no doador o system
      // prompt é o mesmo nas duas etapas, de propósito.
      const promptEtapa2Doador = `Você é um hepatologista sênior e cirurgião de transplante hepático com 20 anos de programa de DOADOR VIVO, atuando como AUDITOR da análise abaixo, feita por um colega menos experiente.

═══ ANÁLISE DO COLEGA ═══
${analiseEtapa1}
═══ FIM DA ANÁLISE ═══
${blocoValidacao}
${contextoComplementar}
${DIMENSOES_ETAPA2_DOADOR}

${FORMATO_SAIDA_DOADOR}`;

      const promptEtapa2Receptor = `Você é um hepatologista sênior e cirurgião de transplante hepático com 20 anos de experiência. Você está revisando a análise clínica abaixo, feita por um colega menos experiente.

      INSTRUÇÃO: Faça o audit clínico nas 11 dimensões abaixo MENTALMENTE (no seu raciocínio interno). NÃO escreva a Fase A na resposta. Entregue DIRETAMENTE a análise final corrigida (Fase B), já com todas as correções incorporadas.

═══ ANÁLISE DO COLEGA ═══
${analiseEtapa1}
═══ FIM DA ANÁLISE ═══
${blocoValidacao}
${contextoComplementar}
════════════════════════════════════════════
DIMENSÕES DO AUDIT (raciocinar internamente, NÃO escrever):
════════════════════════════════════════════

DIMENSÃO 1 — CONSISTÊNCIA DADOS-DIAGNÓSTICO
Para CADA diagnóstico ou problema ativo listado, identifique qual achado específico (lab, imagem ou clínico) o sustenta nos dados do paciente.
- Se não há achado que sustente → REMOVER o diagnóstico ou rebaixar para "em investigação"
- Cirrose: só é diagnóstico CONFIRMADO se há elastografia ≥15 kPa, biópsia F4, ou descompensação documentada (ascite, encefalopatia, HDA varicosa). Se o paciente tem ascite + encefalopatia, cirrose é CONFIRMADA clinicamente — NÃO pedir "confirmação por métodos objetivos"
- CSPH: confirmada se elastografia ≥20-25 kPa, ou varizes de esôfago, ou shunts portossistêmicos em imagem, ou esplenomegalia com plaquetopenia
- CHC: LI-RADS 5 em paciente cirrótico = diagnóstico CONFIRMADO, não precisa de segundo método

DIMENSÃO 2 — EXAMES REDUNDANTES
Para CADA exame solicitado nas recomendações, verifique:
- Este exame já foi realizado recentemente (nos dados)? Se sim → REMOVER
- Se já existe TC trifásica com LI-RADS 5, NÃO solicitar RM "confirmatória"
- Se já existe Doppler hepático documentando TAH ou permeabilidade, NÃO pedir outro Doppler
- Se já existe ColangioRM recente, NÃO solicitar CPRE diagnóstica
- MRCP NÃO é exame separado de RM de abdome em hepatologia

DIMENSÃO 3 — CONDUTAS VS. EVIDÊNCIA LABORATORIAL
Para CADA conduta terapêutica proposta, cruze com os labs:
- Anticoagulação proposta + plaquetas <50.000 → QUESTIONAR (risco hemorrágico)
- Paracentese de alívio + sem ascite volumosa documentada → REMOVER
- Bridge therapy (TACE/RFA) proposta + plaquetas <30.000 → ALERTAR contraindicação relativa
- Propranolol proposto + sem evidência de hipertensão portal → QUESTIONAR
- Se MELD <15, NÃO recomendar "priorizar listagem urgente"

DIMENSÃO 4 — MEDICAÇÃO SEM INDICAÇÃO DOCUMENTADA
Para CADA medicamento sugerido, verifique se a indicação está nos dados:
- Albumina IV → só se albumina sérica documentada <2.5 g/dL
- Rifaximin → só se encefalopatia hepática documentada
- Lactulose → só se encefalopatia hepática documentada
- Propranolol/Carvedilol → indicado se CSPH confirmada (Baveno VII), INDEPENDENTE de EDA. CSPH é confirmada por: descompensação (ascite/encefalopatia/HDA), OU elastografia ≥20-25 kPa, OU varizes. Se CSPH confirmada, NÃO condicionar betabloqueador ao resultado de EDA — iniciar carvedilol e usar EDA para definir necessidade de ligadura complementar
- Antibioticoprofilaxia (norfloxacino) → só se ascite com proteína <1.5 g/dL ou PBE prévia
- Entecavir/Tenofovir → só se HBsAg positivo ou anti-HBc positivo pós-Tx
- Colecistectomia em pré-Tx → NUNCA recomendar isoladamente (é feita durante o transplante)

${dimensao5}

DIMENSÃO 6 — FIDELIDADE FACTUAL (VERIFICAÇÃO CRÍTICA)
Compare CADA valor numérico, data, achado de imagem e evento clínico mencionado na análise com os dados originais do paciente.
- Se um valor de AFP, bilirrubina, MELD ou qualquer lab é citado mas NÃO está nos dados → REMOVER IMEDIATAMENTE
- Se uma consulta ou evento clínico é mencionado mas NÃO está documentado → REMOVER
- Se uma data específica é citada mas não confere com os dados → CORRIGIR
- NUNCA é aceitável fabricar dados. Ausência de dados = "não disponível", NUNCA inventar valores
- CONTAGEM DE EVENTOS: Ao mencionar número de indeferimentos, consultas, internações, procedimentos ou qualquer evento repetível, CONTAR nos dados quantos realmente existem. Se há 1 indeferimento documentado, dizer "1 indeferimento" — NUNCA extrapolar para "múltiplos" ou inventar quantidade. Se a quantidade exata não é verificável nos dados, usar "indeferimento(s) prévio(s)" sem especificar número
- DOCUMENTOS ADMINISTRATIVOS: Ao citar ofícios, pareceres ou cartas, referenciar APENAS os que foram fornecidos nos dados, com número e data exatos quando disponíveis

DIMENSÃO 7 — PROFISSIONALISMO E OBJETIVIDADE
- A análise é apresentada como um hepatologista experiente apresentaria em discussão de caso?
- Linguagem vaga ("considerar avaliar", "talvez investigar") deve ser substituída por condutas diretas
- Focar nos 3-5 problemas mais relevantes, não listar 15 diagnósticos remotos
- Evitar frases como "é importante ressaltar" ou "vale a pena mencionar" — ir direto ao ponto
- Tom: profissional, direto, como discussão entre especialistas
- Calibração de gravidade: ponderar TANTO o histórico cumulativo (descompensações prévias, complicações) QUANTO o estado clínico ATUAL (estabilidade, ambulatorial vs internado, tendências laboratoriais). Paciente com histórico grave mas estável em domicílio pode ser MODERADA-ALTA; o mesmo perfil com descompensação ativa deve ser ALTA ou CRÍTICA

DIMENSÃO 8 — CONSISTÊNCIA BIÓPSIA-IMAGEM
Se o paciente tem DUAS ou mais biópsias com graus de fibrose diferentes:
- Verificar se a imagem ATUAL (US, TC, RM) mostra sinais de hepatopatia avançada (esplenomegalia, contornos irregulares, circulação colateral, vias biliares alteradas)
- Se imagem atual mostra hepatopatia avançada E a biópsia mais recente mostra fibrose baixa (F0-F1): a biópsia menor É ERRO AMOSTRAL — não aceitar como verdade
- A biópsia com MAIOR fibrose + imagem atual têm precedência absoluta
- PROIBIDO: "regressão de fibrose", "melhora histológica", "resposta ao tratamento" quando imagem contradiz
- Se a análise da Etapa 1 aceitou a biópsia menor como verdade → CORRIGIR na análise final

DIMENSÃO 9 — PLAUSIBILIDADE BIOLÓGICA E CINÉTICA TUMORAL
Se o paciente tem exames de imagem sequenciais, verificar se o comportamento das lesões é biologicamente compatível com o diagnóstico:
REGRESSÃO OU ESTABILIDADE SEM TRATAMENTO:
- CHC típico tem tempo de duplicação de 80-120 dias. Se lesão LI-RADS 5 DIMINUIU ou ficou ESTÁVEL por >6 meses SEM tratamento oncológico documentado (TACE, ablação, sistêmico) → SINAL DE ALARME DIAGNÓSTICO. A Etapa 1 DEVE ter questionado o diagnóstico. Se não questionou → CORRIGIR: destacar como achado crítico, listar diferenciais (nódulo displásico de alto grau, adenoma hepatocelular, hemangioma esclerosante, HNF atípica), recomendar revisão multidisciplinar
- Lesão de 5+ cm estável por >12 meses sem tratamento é biologicamente incompatível com colangiocarcinoma intrahepático (sobrevida mediana ~8 meses) → NÃO sugerir CCA como diagnóstico diferencial para lesões com estabilidade prolongada documentada
- LI-RADS classifica PADRÃO DE IMAGEM, não confirma histologia. Cinética incompatível com CHC deve ser explicitamente questionada mesmo com LR-5 confirmado
MELHORA IMPLAUSÍVEL COM TRATAMENTO:
- Resposta completa de CHC avançado (>5cm + invasão vascular) com TKI em <3 meses = IMPLAUSÍVEL
- Resolução de trombose tumoral macrovascular em semanas com terapia sistêmica = IMPLAUSÍVEL
- Redução volumétrica >50% sem terapia locorregional direta em <8 semanas = ALTAMENTE SUSPEITO
- Se melhora implausível detectada: PROIBIDO usar "resposta excepcional", "downstaging bem-sucedido"
- OBRIGATÓRIO: questionar discordância técnica (protocolo diferente, equipamento, erro de laudo), recomendar revisão comparativa DICOM
- Se a análise da Etapa 1 aceitou comportamento implausível sem questionar → CORRIGIR na análise final

DIMENSÃO 10 — COMORBIDADES E DADOS CLÍNICOS BASAIS
Se o campo COMORBIDADES REGISTRADAS contém informações:
- Verificar se CADA comorbidade listada foi considerada na análise e na conduta
- TB prévia + imunossupressão → discutir risco de reativação, necessidade de rastreio (PPD/IGRA)
- Dano musculoesquelético por corticoide (fratura, osteoporose, necrose avascular) → discutir minimização/retirada do corticoide
- DM, HAS, DRC, cardiopatia → impacto na imunossupressão e na elegibilidade para transplante
- Se peso e altura estão disponíveis → usar para cálculos de dose por kg (UDCA, proteína)
- Se alergias estão registradas → verificar que nenhuma recomendação conflita
- Se a análise da Etapa 1 IGNOROU comorbidades registradas → ADICIONAR na análise final com impacto na conduta

DIMENSÃO 11 — COERÊNCIA LÓGICA DAS RECOMENDAÇÕES
Verificar se as recomendações são mutuamente compatíveis e logicamente ordenadas:
- Se uma recomendação DEPENDE do resultado de outra, ela deve ser CONDICIONAL e posterior, não paralela
- Se uma investigação pode MUDAR COMPLETAMENTE a conduta subsequente, ela vem PRIMEIRO
- Exemplos de incoerência a CORRIGIR:
  * "Questionar se é CHC" + "Reapresentar SE por CHC" = CONTRADITÓRIO. Sequência correta: resolver dúvida diagnóstica → SE condicionada ao resultado
  * "Excluir HPoP" + "Terapia ponte (TACE)" como itens paralelos = INCOERENTE. TACE fica condicionada à exclusão de HPoP
  * "Biópsia para definir N3" + "Nova SE com 2 lesões" = PREMATURO. SE depende do resultado da biópsia
- Usar formato condicional: "Se [resultado X] → fazer Y; Se [resultado Z] → fazer W"
- Se a Etapa 1 listou recomendações contraditórias ou logicamente desordenadas → REORGANIZAR com dependências explícitas

════════════════════════════════════════════
ANÁLISE FINAL CORRIGIDA
Incorpore TODAS as correções identificadas no audit interno.
Use EXATAMENTE este formato:
════════════════════════════════════════════

ANÁLISE CLÍNICA — HEPATOLOGIA/TRANSPLANTE HEPÁTICO

1. RESUMO CLÍNICO
[Parágrafo com síntese do caso — nomear TODOS os problemas ativos; até 6 frases]
Problemas ativos: [apenas achados CONFIRMADOS]
Em investigação: [achados que precisam de confirmação]
Gravidade: [LEVE/MODERADA/GRAVE — com justificativa em 1 frase]

2. ANÁLISE DETALHADA
[Função hepática com valores e interpretação]
[Achados de imagem com tipo/data/achados principais — citar o exame]
[Complicações: separar CONFIRMADAS de EM INVESTIGAÇÃO]
[Marcadores tumorais se relevantes]

3. AVALIAÇÃO DE RISCO
[Prognóstico com scores quando disponíveis (MELD, Child-Pugh)]
[Fatores de risco prioritários]
[Sinais de alarme: CADA um vira achado → CONDUTA → reavaliar (o quê e em quanto tempo). Rotular alarme SEM conduta é PROIBIDO]
[SITUAÇÃO ESPECIAL (B316) — se o paciente é candidato a alguma situação especial (ascite/hidrotórax refratário, EH, colangites de repetição, SHP, prurido, CHC...), inclua AQUI o bloco itemizado, DESTACADO (não diluído nas Recomendações), com um elemento por linha e o marcador ✅ PREENCHE / ⚠️ A CONFIRMAR / ❌ NÃO PREENCHE no início — PRESERVE esses marcadores, não os remova (nem rebaixe ❌ para ⚠️: elemento com janela de tempo vencida ou ausência registrada é ❌ NÃO PREENCHE, com o aviso de vulnerabilidade a indeferimento; mas no critério com vias alternativas — ex.: agravo da ascite refratária por intolerância a diurético OU ≥3 paracenteses —, a via não usada NÃO é elemento faltante). Feche com "→ Preenche N de M" e "→ Ganho: MELD ajustado <valor> vs MELD-Na atual <X>". É exceção de MELD, nunca "priorização".]

4. RECOMENDAÇÕES
[Condutas imediatas — numeradas por prioridade clínica]
[Para cada conduta: prefixar com semáforo de confiança]
[Para cada conduta: justificativa em 1 frase com guideline quando a fonte for conhecida com segurança; senão, o racional clínico (B219)]
[Se há incerteza diagnóstica: apresentar cenários A/B com conduta para cada]
[Monitoramento: o quê, quando, por quê]
Semáforo: 🟢 = guideline direto + dados suficientes para decisão unilateral | 🟡 = dados incompletos, evidência indireta, ou conduta que exige discussão multidisciplinar | 🔴 = achados ambíguos, extrapolação, ou causa não identificada

5. CONSIDERAÇÕES ESPECIAIS
[Apenas se relevante: transplante, situação especial, aspectos cirúrgicos]

Urgência: [BAIXA/MÉDIA/ALTA — justificativa + guideline de referência]

REGRAS ABSOLUTAS DA FASE B:
- EXTENSÃO por gravidade (B221): LEVE/BAIXA = 400-600 palavras; GRAVE/ALTA = 800-1200 (em dúvida, a MAIOR). A Fase B CORRIGE, NÃO encurta — pode AUMENTAR a saída.
- NÃO-PERDA (B221): conter/expandir todo achado crítico, valor laboratorial crítico, sinal de alarme e conduta da Etapa 1; só remover o FALSO ou REDUNDANTE.
- NÃO repita informações entre seções
- ANCORAGEM DE CITAÇÃO (B219): só nomeie a sociedade E/OU o ano de uma diretriz (AASLD, EASL, Baveno VII, AST, Portaria 8041 etc.) se essa sigla/ano constar LITERALMENTE de uma "Fonte N" recuperada no CONTEXTO DE GUIDELINES — seja no rótulo (campos "Organização:"/"Ano:"), no TÍTULO da Fonte ou no TEXTO do trecho (consensos como "Baveno VII", "Milão" ou "Portaria 8041" costumam vir no título/corpo, não no campo "Organização:") — OU dos dados do paciente. Copie a sigla e o ano EXATAMENTE como aparecem, NUNCA de memória; mas trate valores genéricos ou vazios ("Other", "undefined", "N/A", em branco) como AUSÊNCIA de fonte — NUNCA os escreva como sociedade/ano. Sem Fonte que sustente a sigla/ano, dê o racional clínico sem inventar nem supor sociedade/ano ausentes do contexto. Esta regra NÃO suprime citação legítima: se a Fonte traz a sigla/ano reais, CITE normalmente; ela só remove o RÓTULO de proveniência não-ancorado ou inútil — a conduta clínica correta NUNCA é removida nem enfraquecida por falta de citação (B219)
- CADA exame solicitado deve ter justificativa
- CADA medicamento deve ter indicação nos dados
- Se um dado não existe no prontuário, escreva "não disponível" — NUNCA invente
- CADA recomendação DEVE iniciar com 🟢, 🟡 ou 🔴 conforme confiança na evidência e completude dos dados disponíveis`;

      const promptEtapa2 = ehDoador ? promptEtapa2Doador : promptEtapa2Receptor;

      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      // M-R2 / B173 (AI_79) — fail-closed: se a Etapa 2 falhar, o pipeline
      // ABORTA com erro (mesmo contrato da Etapa 1). Sem fallback para a
      // Etapa 1 crua — análise nao-auditada nunca e entregue com aparencia
      // de analise plena (CFM 2454/2026, medio risco sob supervisao ativa).
      let data2: {
        content: Array<{ type: string; text: string }>;
        // 🛡️ B223 — avisos da rede de fidelidade anexados pelo backend (opcional).
        avisosFidelidade?: Array<{ tipo: string; mensagem: string }>;
        _proveniencia?: ProvenienciaBackend;
      };
      try {
        data2 = await callAnalyze<{
          content: Array<{ type: string; text: string }>;
          avisosFidelidade?: Array<{ tipo: string; mensagem: string }>;
          _proveniencia?: ProvenienciaBackend;
        }>({
          prompt: promptEtapa2,
          useThinking: true,
          etapa: "self-reflection",
          // esforço: definido no SERVIDOR ("low" na Etapa 2)
          modalidade,
        });
      } catch (err: any) {
        console.error("❌ Erro na Etapa 2:", err.message);
        throw new Error(`API Error Etapa 2: ${err.message}`);
      }

      let analiseEtapa2 = data2.content
        .filter((c: any) => c.type === "text")
        .map((c: any) => c.text)
        .join("\n");

      // 🔧 B375 (AI_156) — Pós-processamento: MELD/Child como condição ATUAL.
      // A rede existe por regra clínica de Leonardo (AI_89): após o Tx a cirrose
      // nativa não existe, então os escores não descrevem o paciente atual.
      // Era CEGA e mordia o texto — destruía o MELD HISTÓRICO pré-Tx (que o
      // B239 pede que seja citado) e deixava dígito órfão ("com 0 de 26",
      // "era e 0 26"), achado por Leonardo na leitura cega do B363.
      // Agora é frase a frase, preserva o histórico e não deixa resto.
      // Ver src/shared/utils/escoresPosTx.ts (12 testes, com os 2 casos reais).
      if (isPosTx) {
        const dataTx = (dadosOriginais || "").match(
          /"dataTransplante"\s*:\s*"(\d{4}-\d{2}-\d{2})"/
        )?.[1];
        const antes = analiseEtapa2;
        analiseEtapa2 = removerEscoresAtuaisPosTx(analiseEtapa2, dataTx);
        if (antes !== analiseEtapa2) {
          console.log(
            "🔧 [B375] menções a MELD/Child como condição atual removidas (pós-Tx)"
          );
        }
      }

      console.log("✅ Etapa 2 concluída! Análise revisada gerada.");
      const analiseObj = criarAnaliseDeTexto(analiseEtapa2, ehDoador);
      // 🛡️ B223 — avisos NÃO-bloqueantes da rede de fidelidade (anexados pelo
      // backend à resposta do passo self-reflection). Só atribui quando há
      // avisos (nunca undefined → Firestore-safe).
      const avisosFidelidade = data2.avisosFidelidade;
      if (Array.isArray(avisosFidelidade) && avisosFidelidade.length > 0) {
        analiseObj.avisosFidelidade = avisosFidelidade;
      }
      // 🏷️ B388 (AI_156) — guarda qual modelo/esforço/versão gerou a análise
      // (o backend já anexava `_proveniencia`; aqui era descartado). Só atribui
      // quando existe (nunca undefined → Firestore-safe).
      const proveniencia = montarProvenienciaGeracao(
        data1._proveniencia,
        data2._proveniencia
      );
      if (proveniencia) {
        analiseObj.proveniencia = proveniencia;
        console.log(
          `🏷️ [B388] gerada por ${proveniencia.modelo} · ${proveniencia.esforco} · versão ${proveniencia.versaoApp}`
        );
      }
      return analiseObj;
    } catch (error) {
      console.error("❌ Erro na API Anthropic:", error);
      throw new Error("Falha ao processar análise com IA");
    }
  };

  /**
   * 📝 Converter texto da IA em objeto AnaliseIA
   */
  const criarAnaliseDeTexto = (
    texto: string,
    ehDoadorParaSelo = false
  ): AnaliseIA => {
    const textoLimpo = texto
      .replace(/```json\n?/g, "")
      .replace(/```\n?/g, "")
      .trim();

    // B261 (AI_117): selo derivado do texto de forma ROBUSTA à deriva de
    // formatação do modelo (markdown/acentos) — fonte única `derivarStatusGeral`.
    // B351 passo 5b (AI_153) — o eixo do selo depende da modalidade. No doador
    // NÃO se chama derivarGravidade/derivarUrgencia: verificado que a regex de
    // gravidade casa com frase incidental ("esteatose de gravidade leve") e
    // produziria selo verde — a falsa tranquilidade que esta modalidade mais
    // comete. O statusGeral é MAPEADO da adequabilidade para os consumidores
    // legados (cor do card, painel de comparação) não caírem no default.
    const adequabilidade = ehDoadorParaSelo
      ? derivarAdequabilidadeDoador(textoLimpo)
      : null;
    const statusGeral = ehDoadorParaSelo
      ? statusGeralDeAdequabilidade(adequabilidade)
      : derivarStatusGeral(textoLimpo);
    // B275 (AI_120) — eixos separados p/ o selo duplo (Gravidade × Urgência).
    const gravidade = ehDoadorParaSelo ? null : derivarGravidade(textoLimpo);
    const urgencia = ehDoadorParaSelo ? null : derivarUrgencia(textoLimpo);

    return {
      id: "",
      pacienteId: "",
      dataAnalise: new Date().toISOString(),
      versao: VERSAO_PROMPT,
      resumo: {
        statusGeral,
        gravidade,
        urgencia,
        adequabilidade,
        principaisAchados: [textoLimpo],
        alertasCriticos: [],
      },
      analises: {
        hepatica: {
          status: "normal",
          enzimas: {
            ast: { valorAtual: 0, status: "normal" },
            alt: { valorAtual: 0, status: "normal" },
            ggt: { valorAtual: 0, status: "normal" },
            fa: { valorAtual: 0, status: "normal" },
          },
          bilirrubinas: {
            total: { valorAtual: 0, status: "normal" },
            direta: { valorAtual: 0, status: "normal" },
            indireta: { valorAtual: 0, status: "normal" },
          },
          sinteseHepatica: {
            albumina: { valorAtual: 0, status: "normal" },
            tap: { valorAtual: 0, status: "normal" },
            inr: { valorAtual: 0, status: "normal" },
          },
          interpretacao: textoLimpo,
        },
        renal: {
          status: "normal",
          creatinina: { valorAtual: 0, status: "normal" },
          ureia: { valorAtual: 0, status: "normal" },
          interpretacao: "",
        },
        imunossupressao: {
          status: "adequado",
          interpretacao: "",
        },
      },
      sugestoes: [],
      tendencias: [],
      correlacoes: [],
    };
  };
  /**
   * 💾 SALVAR ANÁLISE NO FIRESTORE
   */
  // ==============================
  // 🧠 MECR-RAG: Buscar análises aprovadas como few-shot
  // ==============================
  const buscarAnalisesAprovadas = async (
    tipoAtendimento: string,
    diagnosticoBase: string,
    pacienteIdAtual: string
  ): Promise<string> => {
    // 🔒 B351 passo 5b (AI_153) — DOADOR NÃO USA FEW-SHOT. Decisão de Leonardo
    // (05/09), pendente do advogado. Dois motivos, e o segundo é o que torna o
    // gate URGENTE:
    //
    // (1) PRIVACIDADE — a análise do doador cita o CONJUNTO MÍNIMO DO RECEPTOR
    //     (peso, MELD datado, diagnóstico). `minimizarExemploMECR` redige datas,
    //     nomes e identificadores, mas PRESERVA labs e números de propósito — é
    //     o que dá valor pedagógico ao exemplo. Logo, reusar a análise do doador
    //     X como exemplo no prompt do doador Y levaria dados de um RECEPTOR-
    //     TERCEIRO para outro prontuário. O filtro por `tipoAtendimentoAprovado`
    //     (abaixo) separa doador de receptor, mas não resolve isso.
    //
    // (2) CONTAMINAÇÃO — hoje a análise geral do doador ainda é gerada pelo
    //     pipeline do RECEPTOR (system prompt que ORDENA situação especial e
    //     MELD ajustado). Se uma dessas for aprovada, ela vira exemplo-ouro
    //     carimbado "doador_figado" e PROPAGA o enquadramento errado a todos os
    //     doadores futuros — dano persistente em Firestore, não um texto ruim na
    //     tela. O gate fecha essa porta antes de a modalidade ter análise
    //     própria.
    //
    // Gate na FONTE (não no call site) para que qualquer chamador futuro herde.
    // Espelho em AbaAnaliseIA (a aprovação também não marca `aprovada` no
    // doador) — as duas pontas, porque o histórico pode já conter aprovações.
    if (!modalidadeUsaFewShotMECR(tipoAtendimento)) {
      console.log(
        "🔒 MECR-RAG: few-shot desligado nesta modalidade (doador de fígado — B351)"
      );
      return "";
    }
    try {
      const analisesRef = collection(db, "analises-ia");
      const q = query(
        analisesRef,
        where("aprovada", "==", true),
        orderBy("criadoEm", "desc"),
        limit(20)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.log("ℹ️ MECR-RAG: Nenhuma análise aprovada encontrada");
        return "";
      }

      // Filtrar: mesmo tipoAtendimento, excluir paciente atual
      const candidatas = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() } as any))
        .filter(
          (a: any) =>
            a.tipoAtendimentoAprovado === tipoAtendimento &&
            a.pacienteId !== pacienteIdAtual
        );

      if (candidatas.length === 0) {
        console.log("ℹ️ MECR-RAG: Nenhuma análise aprovada do mesmo tipo");
        return "";
      }

      // Ordenar por similaridade de diagnóstico (matching simples)
      const diagLower = (diagnosticoBase || "").toLowerCase();
      const palavrasDiag = diagLower
        .split(/[\s,;]+/)
        .filter((p) => p.length > 3);

      const pontuadas = candidatas.map((a: any) => {
        const diagAprovado = (a.diagnosticoBaseAprovado || "").toLowerCase();
        let score = 0;
        palavrasDiag.forEach((palavra) => {
          if (diagAprovado.includes(palavra)) score += 1;
        });
        // Bonus para match exato
        if (diagLower && diagAprovado === diagLower) score += 10;
        return { ...a, _score: score };
      });

      // Ordenar por score desc, depois por data desc
      pontuadas.sort((a: any, b: any) => {
        if (b._score !== a._score) return b._score - a._score;
        return (b.criadoEm || "").localeCompare(a.criadoEm || "");
      });

      // Pegar top 2
      const selecionadas = pontuadas.slice(0, 2);

      // Formatar como contexto few-shot
      let contexto =
        "\n\n=== ANÁLISES PREVIAMENTE APROVADAS PELO HEPATOLOGISTA ===\n";
      contexto +=
        "As análises abaixo foram validadas como corretas. Use como referência de raciocínio e estilo de conduta.\n\n";

      selecionadas.forEach((a: any, i: number) => {
        // 🔒 B171/M-R5 — minimizar o exemplo de paciente-TERCEIRO antes de
        // injetá-lo no prompt do paciente atual. Prefere `mecrExemplo` (já
        // minimizado na gravação); para o histórico antigo, minimiza na leitura
        // (rede de segurança). Idempotente, então o duplo passo é inócuo.
        const achados =
          a.mecrExemplo?.principaisAchados ??
          minimizarListaMECR(a.resumo?.principaisAchados);
        const condutas =
          a.mecrExemplo?.sugestoes ??
          (Array.isArray(a.sugestoes)
            ? a.sugestoes.map((s: any) => minimizarSugestaoMECR(s))
            : []);
        const diagnostico = minimizarTextoMECR(a.diagnosticoBaseAprovado || "");

        contexto += `--- Caso aprovado ${i + 1} ---\n`;
        contexto += `Tipo: ${
          a.tipoAtendimentoAprovado || "N/A"
        } | Diagnóstico: ${diagnostico || "N/A"}\n`;
        if (achados.length > 0) {
          contexto += `Achados: ${achados.join("; ")}\n`;
        }
        if (a.resumo?.statusGeral) {
          contexto += `Status: ${a.resumo.statusGeral}\n`;
        }
        if (condutas.length > 0) {
          contexto += `Condutas: ${condutas.slice(0, 5).join("; ")}\n`;
        }
        contexto += "\n";
      });

      contexto += "=== FIM ===\n";

      console.log(
        `✅ MECR-RAG: ${selecionadas.length} análise(s) aprovada(s) injetada(s)`
      );
      return contexto;
    } catch (error) {
      console.warn("⚠️ MECR-RAG indisponível:", error);
      return "";
    }
  };
  const salvarAnalise = async (
    pacienteId: string,
    analise: AnaliseIA,
    nomePaciente?: string
  ): Promise<string> => {
    try {
      // 🔐 B142 — Registro de autoria (CFM 2454/2026 Art. 4.V)
      // A análise só é salva se houver médico autenticado no momento da escrita.
      // Sem autoria registrada, não há rastreabilidade e a análise não pode
      // entrar como contexto few-shot do MECR-RAG sem violar cruzamento de prontuários.
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error(
          "Análise não pode ser salva sem médico autenticado (CFM 2454/2026 Art. 4.V)"
        );
      }

      const analisesRef = collection(db, "analises-ia");

      const nomesProibidos = nomePaciente ? [nomePaciente] : [];
      const documento = {
        ...analise,
        pacienteId,
        dataAnalise: new Date().toISOString(),
        versao: VERSAO_PROMPT,
        criadoEm: new Date().toISOString(),
        // 🔐 Campos de autoria — DEPOIS do spread para impedir injeção via parâmetro
        criadoPorUid: currentUser.uid,
        criadoPorEmail: currentUser.email ?? null,
        // 🔒 B171/M-R5 — superfície de reuso few-shot já minimizada na gravação
        // (gravação-limpa). NÃO altera resumo/sugestoes (registro do próprio
        // paciente); cria campo dedicado, preferido por buscarAnalisesAprovadas.
        // Após o spread → sobrescreve qualquer `mecrExemplo` injetado.
        mecrExemplo: {
          principaisAchados: minimizarListaMECR(
            analise.resumo?.principaisAchados,
            nomesProibidos
          ),
          sugestoes: Array.isArray(analise.sugestoes)
            ? analise.sugestoes.map((s: any) =>
                minimizarSugestaoMECR(s, nomesProibidos)
              )
            : [],
        },
      };

      // B229 fase 2 (AI_148): saneia undefined aninhado (Firestore rejeita) —
      // defesa em profundidade agora que o payload carrega documentosRag.
      const docRef = await addDoc(analisesRef, removerUndefined(documento));
      console.log(
        "✅ Análise salva no Firestore:",
        docRef.id,
        "por:",
        currentUser.email
      );

      return docRef.id;
    } catch (error) {
      console.error("❌ Erro ao salvar análise:", error);
      throw new Error("Falha ao salvar análise no banco de dados");
    }
  };

  /**
   * 🎯 FUNÇÃO PRINCIPAL: GERAR ANÁLISE COMPLETA
   */
  const gerarAnalise = async (
    paciente: Paciente
  ): Promise<AnaliseIA | null> => {
    setLoading(true);
    setErro(null);

    try {
      // 1️⃣ Verificar cache (1 análise por dia)
      // ⚠️ DESATIVADO TEMPORARIAMENTE PARA DESENVOLVIMENTO
      console.log("1️⃣ Verificando cache... (DESATIVADO - modo dev)");
      // const cache = await verificarCache(paciente.id);

      // if (!cache.podeGerar) {
      //   setErro(cache.motivoBloqueio || "Análise já realizada hoje");
      //   setLoading(false);
      //   return null;
      // }

      // 2️⃣ Coletar dados do paciente
      console.log("2️⃣ Coletando dados do paciente...");
      setProgresso("Coletando dados do paciente...");
      const dadosAnonimizados: DadosAnonimizados = await coletarDadosCompletos(
        paciente
      );

      // 3️⃣ + 4️⃣ Buscar RAG e gerar prompt EM PARALELO (~4s de ganho)
      console.log("3️⃣ Buscando guidelines + gerando prompt em paralelo...");
      setProgresso("Preparando análise e buscando guidelines...");

      const [contextoRag, promptBase, contextoMECR] = await Promise.all([
        // RAG: buscar guidelines relevantes (~4s). B229 fase 2/B328 (AI_148):
        // devolve o texto (idêntico) + a lista de docs recuperados (proveniência).
        // B351 passo 5b (AI_153) — o doador tem caminho próprio no RAG. Pelo
        // fluxo normal ele cairia no corpus de cirrose e receberia a query de
        // situação especial; o filtro de categoria do Pinecone chega a EXCLUIR
        // o consenso de doador vivo. Ver obterContextoGuidelinesDoador.
        (ehDoadorFigado(dadosAnonimizados.tipoAtendimento)
          ? obterContextoGuidelinesDoador()
          : obterContextoGuidelinesComDocs(dadosAnonimizados))
          .then((ctx) => {
            if (ctx.texto) console.log("✅ Contexto de guidelines obtido!");
            return ctx;
          })
          .catch((ragError) => {
            console.warn(
              "⚠️ RAG indisponível, continuando sem contexto:",
              ragError
            );
            return { texto: "", documentos: [] };
          }),
        // Prompt: gerar prompt especializado (instantâneo)
        // B351 passo 5b (AI_153) — a modalidade escolhe o prompt. O caminho do
        // receptor fica intocado; o do doador não passa por
        // gerarPromptAnaliseGeralRefinado, que injeta blocos de cirrose,
        // pré-transplante e oncologia sem condicional de modalidade.
        Promise.resolve(
          ehDoadorFigado(dadosAnonimizados.tipoAtendimento)
            ? gerarPromptAnaliseDoador(dadosAnonimizados)
            : gerarPromptAnaliseGeralRefinado(dadosAnonimizados)
        ),
        // MECR-RAG: buscar análises aprovadas como few-shot
        buscarAnalisesAprovadas(
          dadosAnonimizados.tipoAtendimento || "nao-especificado",
          dadosAnonimizados.diagnosticoBase || "",
          paciente.id
        ),
      ]);
      console.log("4️⃣ Prompt e guidelines prontos!");
      const prompt = promptBase + contextoRag.texto + contextoMECR;
      // 5️⃣ Chamar API Anthropic
      console.log("5️⃣ Chamando IA para análise...");
      setProgresso("Analisando dados clínicos (Etapa 1/2)...");
      // Passar apenas os dados anonimizados (sem instruções e sem RAG) para a Etapa 2.
      // B256 (AI_115): minimiza p/ o cruzamento — trunca textos longos (laudos/
      // evoluções por extenso, que a Etapa 1 já interpretou), mantendo números e
      // campos estruturados intactos. Corta o grosso dos tokens da Etapa 2 em
      // pacientes com muito dado, sem perder a âncora numérica da auto-revisão.
      const dadosParaValidacao = JSON.stringify(
        // B358-FU5 (AI_156): MELD do exame rotulado "MELD 3.0" (o MELD-Na de
        // alocação vai em `meldNaAlocacaoSP`) — sem isto a Etapa 2 "corrigia"
        // a Etapa 1 para "MELD-Na não disponível".
        minimizarDadosParaCruzamento(examesComMeldRotulado(dadosAnonimizados)),
        null,
        0
      );
      const analise = await chamarAPIAnthropic(
        prompt,
        dadosParaValidacao,
        paciente.tipoAtendimento
      );
      // 6️⃣ Adicionar metadados
      const analiseCompleta: AnaliseIA = {
        ...analise,
        id: "", // Será preenchido após salvar
        pacienteId: paciente.id,
        dataAnalise: new Date().toISOString(),
        versao: VERSAO_PROMPT,
        // 📚 B229 fase 2 / B328 (AI_148) — diretrizes recuperadas do RAG
        // (proveniência). Sempre array; [] se o RAG falhou.
        documentosRag: contextoRag.documentos || [],
      };

      // 🚨 B373 (AI_156) — ANÁLISE GERADA SEM AS DIRETRIZES, EM SILÊNCIO.
      // Quando a busca no RAG falha (rede caiu — aconteceu com Leonardo em
      // 30/10, wi-fi público com DNS fora), o código segue com contexto VAZIO:
      // a análise sai com aparência normal e NADA diz ao médico que ela nasceu
      // sem embasamento nas diretrizes. Ele atende de Rondônia em redes
      // instáveis; isso se repete no uso real.
      // Usa o canal de avisos do B223 (caixa âmbar "Pontos a conferir"), que
      // é SALVO junto da análise — então o registro fica no prontuário, não só
      // na tela. Não-bloqueante: a análise continua sendo entregue.
      if (!contextoRag.texto) {
        analiseCompleta.avisosFidelidade = [
          ...(analiseCompleta.avisosFidelidade || []),
          {
            tipo: "rag_indisponivel",
            mensagem:
              "Esta análise foi gerada SEM consulta às diretrizes — a busca na " +
              "base de referências falhou (rede). O raciocínio clínico e os dados " +
              "do prontuário estão íntegros, mas não houve ancoragem em guideline. " +
              "Vale gerar de novo com a conexão estável antes de usar para decisão " +
              "que dependa de recomendação de diretriz.",
          },
        ];
        console.warn(
          "🚨 [B373] análise gerada SEM contexto de diretrizes (RAG vazio) — aviso anexado."
        );
      }

      // 🛡️ B223-FU (AI_116) — rede de fidelidade nos DADOS-FONTE (imagem×biópsia/
      // EDA/plaquetas), rodando NO FRONT sobre os dados já coletados/anonimizados:
      // ZERO dado novo do paciente em trânsito (LGPD), sinal completo (não
      // truncado). Determinístico, sem IA, NÃO-BLOQUEANTE (try/catch nunca quebra
      // a análise). Acrescenta no máximo 1 aviso consolidado à caixa "Pontos a
      // conferir" (B223), preservando os avisos que o backend já anexou.
      try {
        // B260 (AI_117): passa o TEXTO da análise (valores achatados) p/ o
        // detector listar só os sinais-fonte que a análise NÃO contemplou.
        const avisosFonte = avisosContradicaoFonte(
          dadosAnonimizados,
          extrairTextoAnalise(analise)
        );
        if (avisosFonte.length > 0) {
          analiseCompleta.avisosFidelidade = [
            ...(analiseCompleta.avisosFidelidade || []),
            ...avisosFonte,
          ];
          console.log(
            `🛡️ [B223-FU] ${avisosFonte.length} aviso(s) de contradição na fonte anexado(s).`
          );
        }
      } catch (e) {
        console.warn(
          "⚠️ [B223-FU] detector de contradições na fonte falhou (não-bloqueante):",
          e
        );
      }

      // 7️⃣ Salvar no Firestore
      console.log("7️⃣ Salvando análise...");
      setProgresso("Salvando análise...");
      const analiseId = await salvarAnalise(
        paciente.id,
        analiseCompleta,
        paciente.nome
      );
      analiseCompleta.id = analiseId;

      // 8️⃣ Atualizar estado
      setAnaliseAtual(analiseCompleta);
      setRascunho(""); // B389 — o texto final substitui o rascunho
      setLoading(false);

      console.log("🎉 Análise completa gerada com sucesso!");
      setProgresso("");
      return analiseCompleta;
    } catch (error: any) {
      console.error("❌ Erro ao gerar análise:", error);

      // Mensagem amigável para falha de conexão
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError")
      ) {
        setErro("Falha de conexão. Verifique sua internet e tente novamente.");
      } else {
        setErro(error.message || "Erro desconhecido ao gerar análise");
      }

      setRascunho(""); // B389 — rascunho nunca sobrevive a uma falha
      setLoading(false);
      return null;
    }
  };

  /**
   * 📜 BUSCAR ÚLTIMA ANÁLISE DO PACIENTE
   */
  const buscarUltimaAnalise = async (
    pacienteId: string
  ): Promise<AnaliseIA | null> => {
    try {
      const analisesRef = collection(db, "analises-ia");
      const q = query(
        analisesRef,
        where("pacienteId", "==", pacienteId),
        orderBy("dataAnalise", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        ...doc.data(),
        id: doc.id,
      } as AnaliseIA;
    } catch (error) {
      console.error("❌ Erro ao buscar última análise:", error);
      return null;
    }
  };

  /**
   * 📊 BUSCAR HISTÓRICO DE ANÁLISES
   */
  const buscarHistoricoAnalises = async (
    pacienteId: string,
    limite: number = 10
  ): Promise<AnaliseIA[]> => {
    try {
      const analisesRef = collection(db, "analises-ia");
      const q = query(
        analisesRef,
        where("pacienteId", "==", pacienteId),
        orderBy("dataAnalise", "desc"),
        limit(limite)
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as AnaliseIA[];
    } catch (error) {
      console.error("❌ Erro ao buscar histórico:", error);
      return [];
    }
  };

  return {
    // Estados
    loading,
    erro,
    progresso,
    rascunho, // B389
    analiseAtual,

    // Funções
    gerarAnalise,
    verificarCache,
    buscarUltimaAnalise,
    buscarHistoricoAnalises,
  };
}
